import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  Alert,
  CodeEditor,
  Flex,
  FlexColumn,
  LoadingButton,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import { Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Controller, UseFormReturn } from 'react-hook-form';
import useEnvironments from 'shared/hooks/useEnvironments';
import * as api from 'shared/services/api';
import {
  AddTestForm,
  ConvertQueryApi,
  ConvertQueryApiResult,
  Resource,
  ValidateCustomSqlTestApi,
  ValidateCustomSqlTestResult,
} from 'shared/types';
import { CUSTOM_SQL_CLOUD_ERROR } from './constants';
import CustomQueryResultsPreview from './CustomQueryResultsPreview';
import {
  applyCloudValidationResult,
  clearCustomQueryValidation,
  getCustomSqlCloudErrorCandidates,
  getCustomSqlCloudErrorCode,
  getCustomSqlPreviewSampleLimit,
  getCustomSqlValidateErrorMessage,
  previewCustomSqlTestStatus,
} from './customSqlCloudTest';
import WizardConfigurationStage from './WizardConfigurationStage';

const NOTE_TEXT =
  "Note: The query syntax is validated, however Elementary doesn't have access to your data and cannot execute the query on your behalf. It is recommended to run the query independently and review the results.";

const CONFIG_PANE_WIDTH = 560;
const QUERY_PANE_MAX_WIDTH = 960;

const Wrapper = styled(Flex)`
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const QueryPane = styled(FlexColumn)`
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border-right: 1px solid ${({ theme }) => theme.palette.common.gray8};
`;

const QueryPaneContent = styled(FlexColumn)`
  width: 100%;
  max-width: ${QUERY_PANE_MAX_WIDTH}px;
  margin-inline: auto;
  min-height: 0;
  flex: 1;
  padding: 24px;
  gap: 16px;
  overflow: hidden;
`;

const ConfigPane = styled(FlexColumn)`
  width: ${CONFIG_PANE_WIDTH}px;
  flex-shrink: 0;
  min-height: 0;
  overflow: auto;
`;

const EditorWrapper = styled(FlexColumn)<{ $hasError?: boolean }>`
  flex: 1;
  min-height: 0;
  border-radius: 8px;
  ${({ $hasError, theme }) =>
    $hasError && `outline: 1px solid ${theme.palette.common.red};`}
`;

type Props = {
  methods: UseFormReturn<AddTestForm>;
  singularTestPaths?: string[];
  tableResourcesData?: Resource[];
  codeType?: ConvertQueryApi['code_type'];
  editMode?: boolean;
  isDataTestsEnabled: boolean;
};

const AddCustomQueryTestWizardStages = ({
  methods,
  singularTestPaths = [],
  tableResourcesData = [],
  codeType = 'sql',
  editMode,
  isDataTestsEnabled,
}: Props) => {
  const { selectedEnvId } = useEnvironments();
  const customQuery = methods.watch('customQuery');
  const customQueryConverted = methods.watch('customQueryConverted');
  const destination = methods.watch('customQueryDestination');
  const severity = methods.watch('severity');
  const lastValidatedQueryRef = useRef('');
  const isCloudDestination = isDataTestsEnabled && destination === 'cloud';
  const isPrValidated =
    !!customQueryConverted && lastValidatedQueryRef.current === customQuery;
  const isCloudValidated =
    !!methods.watch('customSqlValidated') &&
    (lastValidatedQueryRef.current === customQuery ||
      lastValidatedQueryRef.current === '');
  const isValidated = isCloudDestination ? isCloudValidated : isPrValidated;

  const {
    mutate: convertQuery,
    isLoading: isConvertQueryLoading,
    error: convertQueryError,
    reset: resetConvertQuery,
  } = useMutation<ConvertQueryApiResult, MutationError, ConvertQueryApi>(
    (value) => api.convertQuery(value, selectedEnvId!),
    {
      onSuccess: (res, variables) => {
        if (methods.getValues('customQuery') === variables.sql_query) {
          lastValidatedQueryRef.current = variables.sql_query;
          methods.setValue('customQueryConverted', res.converted_sql_query);
        }
      },
    },
  );

  const {
    mutate: validateCustomSql,
    isLoading: isValidateCustomSqlLoading,
    error: validateCustomSqlError,
    reset: resetValidateCustomSql,
    data: validateCustomSqlResult,
  } = useMutation<
    ValidateCustomSqlTestResult,
    MutationError,
    ValidateCustomSqlTestApi
  >((value) => api.validateCustomSqlTest(value, selectedEnvId!), {
    onSuccess: (res, variables) => {
      if (methods.getValues('customQuery') !== variables.sql) {
        return;
      }
      if (res.dry_run_error) {
        clearCustomQueryValidation(methods);
        return;
      }
      lastValidatedQueryRef.current = variables.sql;
      applyCloudValidationResult(
        methods,
        res.referenced_assets || [],
        res.requires_asset_selection,
        res.primary_asset_id,
      );
    },
    onError: (error) => {
      const code = getCustomSqlCloudErrorCode(error);
      if (code === CUSTOM_SQL_CLOUD_ERROR.ASSET_SELECTION_REQUIRED) {
        lastValidatedQueryRef.current = methods.getValues('customQuery');
        applyCloudValidationResult(
          methods,
          getCustomSqlCloudErrorCandidates(error),
          true,
          null,
        );
      }
    },
  });

  const handleQueryChange = (value: string) => {
    if (lastValidatedQueryRef.current !== value) {
      lastValidatedQueryRef.current = '';
      clearCustomQueryValidation(methods);
      methods.clearErrors('customQuery');
      resetConvertQuery();
      resetValidateCustomSql();
    }
  };

  useEffect(() => {
    if (customQuery !== lastValidatedQueryRef.current) {
      resetConvertQuery();
      resetValidateCustomSql();
    }
  }, [customQuery, resetConvertQuery, resetValidateCustomSql]);

  const handleValidate = () => {
    resetConvertQuery();
    resetValidateCustomSql();
    methods.clearErrors('customQuery');
    if (isCloudDestination) {
      clearCustomQueryValidation(methods);
      validateCustomSql({
        sql: customQuery,
        dry_run: true,
        sample_limit: getCustomSqlPreviewSampleLimit(
          methods.getValues('sampleRowLimit'),
        ),
      });
      return;
    }
    convertQuery({
      sql_query: customQuery,
      code_type: codeType,
    });
  };

  const dryRunError =
    isCloudDestination && !validateCustomSqlError
      ? validateCustomSqlResult?.dry_run_error
      : undefined;
  const cloudErrorMessage =
    validateCustomSqlError &&
    getCustomSqlCloudErrorCode(validateCustomSqlError) !==
      CUSTOM_SQL_CLOUD_ERROR.ASSET_SELECTION_REQUIRED
      ? getCustomSqlValidateErrorMessage(validateCustomSqlError)
      : dryRunError || undefined;
  const prErrorMessage = convertQueryError
    ? convertQueryError.response?.data?.extra?.error || 'Error processing query'
    : undefined;
  const errorMessage = isCloudDestination ? cloudErrorMessage : prErrorMessage;
  const queryFieldError = methods.formState.errors.customQuery?.message;
  const isValidateLoading = isCloudDestination
    ? isValidateCustomSqlLoading
    : isConvertQueryLoading;
  const showQueryValidated = isValidated && !errorMessage;
  const previewStatus = previewCustomSqlTestStatus(
    validateCustomSqlResult?.result_rows?.length ?? 0,
    severity,
  );

  return (
    <Wrapper>
      <QueryPane>
        <QueryPaneContent>
          <FlexColumn sx={{ gap: 1 }}>
            <Typography sx={{ fontWeight: 'bold' }}>
              {editMode ? 'Edit custom query' : 'Create custom query'}
            </Typography>
            <Typography fontSize="14px">
              {`Write down a SELECT statement that returns the "failing" records - ones
            that disprove your assertion. The test will pass if the query returns
            zero records.`}
              <br />
              Please use the full table names (with database and schema names).
            </Typography>
          </FlexColumn>

          {!isCloudDestination && <Alert severity="info">{NOTE_TEXT}</Alert>}

          <EditorWrapper $hasError={!!queryFieldError}>
            <Controller
              name="customQuery"
              control={methods.control}
              render={({ field }) => (
                <CodeEditor
                  {...field}
                  mode="sql"
                  height="100%"
                  onChange={(value) => {
                    field.onChange(value);
                    handleQueryChange(value);
                  }}
                />
              )}
            />
          </EditorWrapper>

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          <Flex
            sx={{ alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}
          >
            {!!queryFieldError && !errorMessage && (
              <OverflowTooltip
                sx={{ maxWidth: '400px' }}
                typographySx={{ color: 'common.red', fontSize: '11px' }}
              >
                {queryFieldError}
              </OverflowTooltip>
            )}
            {showQueryValidated && (
              <Typography sx={{ color: 'common.green100', fontSize: '13px' }}>
                Query validated
              </Typography>
            )}
            <LoadingButton
              size="medium"
              variant="outlined"
              loading={isValidateLoading}
              disabled={!customQuery?.trim()}
              onClick={handleValidate}
            >
              {isCloudDestination ? 'Run query' : 'Validate'}
            </LoadingButton>
          </Flex>

          {isCloudDestination &&
            isValidated &&
            validateCustomSqlResult?.result_columns != null && (
              <CustomQueryResultsPreview
                columns={validateCustomSqlResult.result_columns}
                rows={validateCustomSqlResult.result_rows ?? []}
                status={previewStatus}
              />
            )}
        </QueryPaneContent>
      </QueryPane>

      <ConfigPane>
        <WizardConfigurationStage
          methods={methods}
          singularTestPaths={singularTestPaths}
          tableResourcesData={tableResourcesData}
          isRequiredDataAccessTest={isDataTestsEnabled}
        />
      </ConfigPane>
    </Wrapper>
  );
};

export default AddCustomQueryTestWizardStages;
